import React from "react";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import ListSubheader from "@mui/material/ListSubheader";
import Divider from "@mui/material/Divider";
import MenuItem from "@mui/material/MenuItem";
import { fetchMenuItemsCached } from "../utils/menuService";

export default function FilterSelect({
  label,
  menuType,
  value,
  onChange,
  multiple = false,
  width = "25ch",
  fullWidth = false,
  sx: textFieldSx = {},
  error = false,
  helperText = "",
  disabled = false,
  required = false,
  placeholder,
}) {
  const [options, setOptions] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [inputValue, setInputValue] = React.useState("");

  const dropdownWidth = React.useMemo(() => {
    if (typeof width === "string" && width.endsWith("ch")) {
      const n = parseFloat(width);
      if (!Number.isNaN(n)) return `${n * 1.6}ch`;
    }
    return width;
  }, [width]);

  const getLabel = React.useCallback(
    (opt) => (opt?.label ?? String(opt?.value ?? opt?.id ?? "")),
    []
  );

  const asLabel = React.useCallback(
    (opt) => {
      if (opt == null) return "";
      if (typeof opt === "string" || typeof opt === "number") return String(opt);
      return getLabel(opt);
    },
    [getLabel]
  );

  // Load options when opened OR when a preset value exists
  React.useEffect(() => {
    let mounted = true;
    const load = async () => {
      const hasSelected = multiple
        ? Array.isArray(value) && value.length > 0
        : value != null && value !== "";
      if (!open && !hasSelected) return;
      if (options.length === 0 && !loading) {
        setLoading(true);
        const items = await fetchMenuItemsCached(menuType);
        if (mounted) setOptions(items || []);
        setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [open, menuType, value, multiple]); // eslint-disable-line

  const valueToOption = React.useMemo(() => {
    if (multiple) {
      const vals = Array.isArray(value) ? value : [];
      return options.filter((o) => vals.includes(o.value ?? o.id));
    } else {
      if (value == null || value === "") return null;
      return options.find((o) => (o.value ?? o.id) === value) || null;
    }
  }, [value, options, multiple]);

  // Fallback selection(s) so label shows before options resolve
  const selectedSingle = React.useMemo(() => {
    if (multiple) return null;
    if (valueToOption) return valueToOption;
    if (value == null || value === "") return null;
    return { id: value, value, label: String(value) };
  }, [multiple, valueToOption, value]);

  const selectedMultiple = React.useMemo(() => {
    if (!multiple) return [];
    const vals = Array.isArray(value) ? value : [];
    const found = options.filter((o) => vals.includes(o.value ?? o.id));
    const missing = vals
      .filter((v) => !found.some((o) => (o.value ?? o.id) === v))
      .map((v) => ({ id: v, value: v, label: String(v) }));
    return [...found, ...missing];
  }, [multiple, value, options]);

  // Keep the text field showing a label in single-select mode
  React.useEffect(() => {
    if (!multiple) {
      setInputValue(selectedSingle ? asLabel(selectedSingle) : "");
    }
  }, [multiple, selectedSingle, asLabel]);

  const handleChange = (event, newValue) => {
    if (multiple) {
      const vals = (newValue || []).map((v) => v.value ?? v.id ?? v);
      onChange({ target: { value: vals } });
    } else {
      const val = newValue ? newValue.value ?? newValue.id ?? newValue : "";
      onChange({ target: { value: val } });
      setOpen(false);
    }
  };

  return (
    <Autocomplete
      size="small"
      open={open}
      onOpen={() => setOpen(true)}
      onClose={(e, reason) => {
        if (reason === "selectOption" && multiple) return;
        setOpen(false);
        if (multiple) setInputValue("");
      }}
      multiple={multiple}
      disableCloseOnSelect={!!multiple}
      options={options}
      loading={loading}
      getOptionLabel={(opt) => asLabel(opt)}
      isOptionEqualToValue={(opt, val) =>
        (opt?.value ?? opt?.id) === (val?.value ?? val?.id ?? val)
      }
      value={multiple ? selectedMultiple : selectedSingle}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={(event, newInput) => {
        setInputValue(newInput);
      }}
      componentsProps={{
        paper: {
          sx: {
            width: dropdownWidth,
          },
        },
      }}
      filterSelectedOptions
      renderInput={(params) => {
        const useFullWidth = fullWidth || width === "100%";
        return (
          <TextField
            {...params}
            label={label}
            type="search"
            size="small"
            fullWidth={useFullWidth}
            sx={{ m: 0, width: useFullWidth ? undefined : width, ...textFieldSx }}
            inputProps={{
              ...params.inputProps,
              style: { color: "#000" },
            }}
            error={error}
            helperText={helperText}
            disabled={disabled}
            required={required}
            placeholder={placeholder}
          />
        );
      }}
      renderOption={(props, option) => (
        <MenuItem
          {...props}
          key={option.id ?? option.value ?? option.label}
          sx={{ alignItems: 'flex-start' }}
          title={asLabel(option)}
        >
          {multiple && (
            <Checkbox
              checked={(value || []).includes(option.value ?? option.id)}
              size="small"
              sx={{ mr: 1, mt: 0.3 }}
            />
          )}
          <span>{asLabel(option)}</span>
        </MenuItem>
      )}
      renderGroup={(params) => (
        <>
          <ListSubheader>{/* spacer for future grouping/search header */}</ListSubheader>
          <Divider />
          {params.children}
        </>
      )}
    />
  );
}